import { useContext, useMemo } from 'react';
import { UserProfileContext } from '@texas-education-exchange/edx-portal-shared';
import { useLocation } from 'react-router-dom';
import routes from '../core/routes';
import { useGetUserSyncJobQuery } from '../services/apis/userSyncAPI';
import {
  collectRegionHints,
  isCommunityHomePage,
  isPlatformAdmin,
  resolveTechSupportUrl,
} from '../utils/helpers';
import { isSyncEnabled, shouldShowSyncBanner } from '../utils/userSyncBannerHelper';
import { useConfiguration } from './configuration.hooks';

// Reads sync status once per page load. Completing the sync in the tech
// console shows up here on the next visit or refresh; a live update is not
// required. The RTK cache is not persisted, so a refresh always re-requests.
const useUserSyncBanner = ({ auth, userData }) => {
  const location = useLocation();
  const { userProfile } = useContext(UserProfileContext);
  const { userSync } = useConfiguration();

  const platformAdmin = isPlatformAdmin(
    auth?.user?.profile?.role,
    userSync?.platformAdminRole,
  );
  const onHomePage = isCommunityHomePage(location.pathname, routes.dashboard.url);
  const tenantId = userProfile?.tenantId || auth?.user?.profile?.tenantid;
  const shouldFetch = Boolean(platformAdmin && onHomePage);

  const {
    data,
    isFetching,
    isError,
    isSuccess,
  } = useGetUserSyncJobQuery(undefined, {
    skip: !shouldFetch,
  });

  const statusKnown = shouldFetch && isSuccess && !isFetching && !isError;
  const showSyncBanner = shouldShowSyncBanner({
    platformAdmin,
    onHomePage,
    statusKnown,
    syncEnabled: isSyncEnabled(data?.data),
  });

  const supportUrl = useMemo(() => {
    return resolveTechSupportUrl(collectRegionHints({
      organizationName: userProfile?.selectedTenant?.organizationName,
      selectedTenant: userProfile?.selectedTenant,
      tenantStatus: userData?.data?.tenant_status,
      tenantId,
    }), userSync);
  }, [userProfile, userData, tenantId, userSync]);

  return {
    showSyncBanner,
    supportUrl,
  };
};

export default useUserSyncBanner;
